import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Switch,
  Route,
  Redirect,
  RouteProps,
  useLocation,
} from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import { Sidebar, Header, AccountIcon, AppsIcon } from './components';
import { AnalyticsIcon } from './components/Icons/AnalyticsIcon';
import { Account, Apps, Teams, Users } from './pages';
import { Analytics } from './pages/Analytics/Analytics';
import { GoogleCallback } from './pages/GoogleCallback/GoogleCallback';
import { MyOrganization } from './pages/MyOrganization/MyOrganization';
import { refreshToken } from './store/auth/auth.actions';
import { RootState } from './store/store';
import { RouteI, User } from './types';
import { SIGNUP_LINK } from './constants/config';
import { getCurrentPath } from './components/utils';
import { Stats } from './pages/Analytics/Stats';
import { MyDeals } from './pages/MyDeals/MyDeals';
import { DetailedView } from './pages/MyDeals/DetailedView/DetailedView';
import { MyDealsIcon } from './components/Icons/MyDealsIcon';
import PersonalQForIframe from './components/PersonalQForIframe/PersonalQForIframe';
import { TeamAnalyticsIcon } from './components/Icons/TeamAnalyticsIcon';
import { AccountSettingsIcon } from './components/Icons/AccountSettingsIcon';
import { TeamQ } from './pages/TeamQ/TeamQ';
import { Submenu } from './components/Submenu/Submenu';
import { AccountSettings } from './pages/AccountSettings/AccountSettings';
import { TeamsForRedirect } from './pages/TeamsForRedirect/TeamsForRedirect';
import { DealsForRedirect } from './pages/MyDeals/PageToRedirect/DealsForRedirect';
import { OnboardingModal } from './components/OnboardingModal/OnboardingModal';
import { Billing } from './pages/Billing/Billing';
import { ExtensionNIWarnModal } from './components/ExtensionNIWarnModal/ExtensionNIWarnModal';
import { AddInfoModal } from './components/AddInfoModal/AddInfoModal';
import { NonPaidUserModal } from './components/NonPaidUserModal/NonPaidUserModal';

export const ROUTES: RouteI[] = [
  {
    path: '/orgs/:orgId/users/:userId/deals/:threadId?',
    Component: DealsForRedirect,
    label: '',
    tooltip: '',
    roles: ['dealmaker', 'owner', 'admin', 'superAdmin'],
  },
  {
    path: '/orgs/:orgId/users/:userId/engagements',
    Component: MyDeals,
    label: 'My Engagements ',
    tooltip: 'My Engagements',
    Icon: MyDealsIcon,
    roles: ['dealmaker', 'owner', 'admin', 'superAdmin'],
  },
  {
    path:
      '/orgs/:orgId/users/:userId/engagements/:engagementId/threads/:branchId',
    Component: DetailedView,
    label: 'My Engagements',
    tooltip: 'My Engagements',
    roles: ['dealmaker', 'owner', 'admin', 'superAdmin'],
  },
  {
    path: '/orgs/:orgId/users/:userId/analytics/email-stats',
    Component: Stats,
    label: 'My Performance Q  ',
    tooltip: 'My Performance Q',
    roles: ['dealmaker', 'owner', 'admin', 'superAdmin'],
  },
  {
    path: '/orgs/:orgId/users/:userId/performanceq',
    Component: Stats,
    label: 'My Performance Q  ',
    tooltip: 'My Performance Q',
    Icon: AnalyticsIcon,
    roles: ['dealmaker', 'owner', 'admin', 'superAdmin'],
  },
  {
    path: `/orgs/:orgId/users/:userId/team-analytics/:page?`,
    Component: TeamQ,
    label: 'Overview',
    tooltip: 'Team Q',
    Icon: TeamAnalyticsIcon,
    roles: ['owner', 'admin', 'superAdmin'],
  },
  {
    path: `/orgs/:orgId/admin-settings/apps`,
    Component: Apps,
    label: 'Apps',
    tooltip: 'Apps',
    roles: ['owner', 'admin', 'superAdmin'],
  },
  {
    path: `/orgs/:orgId/admin-settings/teams`,
    Component: Teams,
    label: 'Teams',
    tooltip: 'Teams',
    roles: ['owner', 'admin', 'superAdmin'],
  },
  {
    path: `/orgs/:orgId/users/:userId/teams`,
    Component: TeamsForRedirect,
    label: '',
    tooltip: '',
    roles: ['dealmaker', 'owner', 'admin', 'superAdmin'],
  },
  {
    path: '/orgs/:orgId/admin-settings/organization',
    Component: MyOrganization,
    label: 'My Organization',
    tooltip: 'My Organization',
    roles: ['owner', 'admin', 'superAdmin'],
  },
  {
    path: '/orgs/:orgId',
    Component: MyOrganization,
    label: 'My Organization',
    tooltip: 'My Organization',
    roles: ['owner', 'admin', 'superAdmin'],
  },
  {
    path: '/orgs/:orgId/users/:userId/personalq',
    Component: Analytics,
    label: 'Performance Q',
    tooltip: 'Analytics',
    roles: ['dealmaker', 'owner', 'admin', 'superAdmin'],
  },
  {
    path: '/orgs/:orgId/users/:userId/analytics/personalq',
    Component: Analytics,
    label: 'Performance Q ',
    tooltip: 'Analytics',
    roles: ['dealmaker', 'owner', 'admin', 'superAdmin'],
  },
  {
    path: '/orgs/:orgId/users/:userId/personalq_competence',
    Component: PersonalQForIframe,
    label: '',
    tooltip: '',
    roles: ['dealmaker', 'owner', 'admin', 'superAdmin'],
    isIframe: true,
  },
  {
    path: `/orgs/:orgId/users/:userId/apps`,
    Component: Apps,
    label: 'Apps',
    Icon: AppsIcon,
    tooltip: 'My Apps',
    roles: ['dealmaker'],
  },
  {
    path: `/orgs/:orgId/users/:userId`,
    Component: Account,
    label: 'Account',
    Icon: AccountIcon,
    tooltip: 'My Account',
    roles: ['dealmaker', 'owner', 'admin', 'superAdmin'],
  },
  {
    path: `/orgs/:orgId/admin-settings`,
    Component: AccountSettings,
    label: 'Admin Settings',
    Icon: AccountSettingsIcon,
    tooltip: 'Admin Settings',
    roles: ['owner', 'admin', 'superAdmin'],
  },
  {
    path: `/orgs/:orgId/admin-settings/users`,
    Component: Users,
    label: 'Users',
    tooltip: 'Users',
    roles: ['owner', 'admin', 'superAdmin'],
  },
  {
    path: `/orgs/:orgId/users`,
    Component: Users,
    label: 'Users',
    tooltip: 'Users',
    roles: ['owner', 'admin', 'superAdmin'],
  },
  {
    path: '/orgs/:orgId/admin-settings/billing',
    Component: Billing,
    label: 'Billing',
    tooltip: 'Billing',
    roles: ['owner', 'superAdmin'],
  },
];

interface ProtectedRouteProps extends RouteProps {
  roles: string[];
  user: User | null;
  children: JSX.Element;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  roles,
  user,
  children,
  ...props
}: ProtectedRouteProps): JSX.Element | null => {
  if (!localStorage.getItem('refresh')) return <Redirect to="/" />;

  return roles.includes(user?.role as string) && user?.isEmailVerified ? (
    <Route {...props}>
      {({ match }) => (
        <CSSTransition timeout={500} classNames="routes" in={match != null}>
          {children}
        </CSSTransition>
      )}
    </Route>
  ) : null;
};

export const AppRouting: React.FC = (): JSX.Element | null => {
  const { user, authError } = useSelector((state: RootState) => state.auth);

  const { dealsNoData } = useSelector((state: RootState) => state.deals);

  const dispatch = useDispatch();

  const { pathname } = useLocation();

  const { boot } = useIntercom();

  const currentRoute = ROUTES.find(
    ({ path }) => getCurrentPath(path, user) === pathname
  );

  useEffect(() => {
    if (!user && localStorage.getItem('token')) {
      if (authError.refresh) {
        window.location.href = `${SIGNUP_LINK}/logout`;
      }
      dispatch(refreshToken());
    }
  }, [authError, user, dispatch]);

  useEffect(() => {
    if (user && user.userHash) {
      const userInfo = {
        userId: `${user.orgId}|${user.userId}`,
        name: user.name,
        email: user.email,
        userHash: user.userHash,
        customAttributes: {
          userId: user.userId,
          orgId: user.orgId,
        },
      };

      boot(userInfo);
    } // eslint-disable-next-line
  }, [user?.userId]);

  const defineWrapperClassName = () => {
    let result = 'page';
    if (currentRoute?.label === 'Performance Q  ') {
      result = 'analytics-stats__page';
    } else if (currentRoute?.label === 'My Engagements') {
      result = 'my-deals-page';
    } else if (currentRoute?.label === 'My Engagements ') {
      result = 'my-deals-page-darker';
    } else if (currentRoute?.label === 'Billing') {
      result = 'app-sumo-page';
    } else if (currentRoute?.isIframe) {
      result = '';
    }
    return result;
  };

  const defineContentClassName = () => {
    let result = 'page__content';
    if (currentRoute?.label === 'Stats') {
      result = 'analytics-stats__page-content';
    } else if (currentRoute?.label === 'My Engagements') {
      result = 'my-deals-page-content';
    } else if (currentRoute?.label === 'Billing') {
      result = 'app-sumo-page-contet';
    } else if (currentRoute?.isIframe) {
      result = '';
    }
    return result;
  };

  const conditionalPageClassName = defineWrapperClassName();

  const conditionalContentClassName = defineContentClassName();

  const isEngagementsNoDatacheck =
    currentRoute?.label?.includes('My Engagements ') && dealsNoData;

  const conditionalHeader =
    currentRoute?.path.includes('/admin-settings') ||
    currentRoute?.label === 'Account' ||
    currentRoute?.label === 'My Engagements' ||
    isEngagementsNoDatacheck ? null : (
      <Header />
    );

  return (
    <div className={currentRoute?.isIframe ? '' : 'App'}>
      <Sidebar />
      <Submenu />
      <ExtensionNIWarnModal />
      <AddInfoModal />
      <OnboardingModal />
      <NonPaidUserModal />
      <div className={conditionalPageClassName}>
        {conditionalHeader}
        <div className={conditionalContentClassName}>
          <TransitionGroup>
            <Switch>
              {ROUTES.map((route) => {
                const { path, Component, roles } = route;
                return (
                  <ProtectedRoute
                    roles={roles}
                    key={path}
                    exact
                    path={path}
                    user={user}
                  >
                    <Component />
                  </ProtectedRoute>
                );
              })}
              <Route path="/google-callback">
                <GoogleCallback />
              </Route>
              <Route path="*">
                <Redirect to="/not-found" />
              </Route>
            </Switch>
          </TransitionGroup>
        </div>
      </div>
    </div>
  );
};
